import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import {
  Collapse,
  TextTitle,
  useCollapseState,
  ValidatedBootstrapPanel,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { useResponsiveStyles } from '@bingads-webui-campaign-react/common-hooks';
import { useWizardService } from '@bingads-webui-react/wizard';

import { getExtensionsStepStyles } from './styles';
import { StepHeader } from '../../shared/step-header';

const ExtensionsStepInternal = (props) => {
  const {
    data,
    onDataChange,
    error,
    permissions,
  } = props;
  const {
    serverErrors = [],
    systemErrors = [],
  } = error || {};
  const {
    campaignId,
    campaignName,
    adExtensionsAssociations,
    loaded,
  } = data;

  const { state } = useWizardService();
  const {
    adExtensionsAssociationsFactory,
    adExtensionsDynamicFeatures,
  } = state.context.wizardData;

  const activity = useContext(ActivityContext);
  const getStyles = useResponsiveStyles(getExtensionsStepStyles);
  const i18n = useContext(I18nContext);

  const onUpdateExtensionsAssociations = useCallback((extensionType, associations) => {
    activity.signal(`onExtensionsUpdate - ${JSON.stringify({
      extensionType,
      extensionAssociations: associations,
    })}`);
    onDataChange({
      data: {
        ...data,
        adExtensionsAssociations: {
          ...adExtensionsAssociations,
          [extensionType]: associations,
        },
      },
      isValid: true,
    });
  }, [adExtensionsAssociations]); // eslint-disable-line react-hooks/exhaustive-deps

  const getAdExtensionsSections = useCallback(
    extensionTypes => adExtensionsAssociationsFactory.extensionsAssociationsFactory(campaignId, campaignName, extensionTypes, adExtensionsDynamicFeatures)
      .reduce((memo, {
        extensionType,
        title,
        topic,
        details,
        AssociationPanel,
      }) => {
        const sectionKey = extensionType;
        const associations = adExtensionsAssociations[extensionType];

        const panel = (
          <Collapse.Panel
            key={sectionKey}
            title={<TextTitle titleKey={title} topicKey={topic} details={details(i18n, associations)} />}
          >
            <AssociationPanel
              i18n={i18n}
              extensionsAssociations={adExtensionsAssociations}
              onUpdateExtensionsAssociations={onUpdateExtensionsAssociations}
            />
          </Collapse.Panel>

        );
        memo.panels.push(panel);
        memo.sectionsInfo.allSections.push(sectionKey);

        return memo;
      }, { panels: [], sectionsInfo: { allSections: [] } }),
    [adExtensionsAssociations, adExtensionsAssociationsFactory, adExtensionsDynamicFeatures, campaignId, campaignName, i18n, onUpdateExtensionsAssociations]
  );

  const { suggestedObjectiveExtensions = [], otherObjectiveExtensions = [] } = useMemo(() =>
    adExtensionsAssociationsFactory.getObjectiveExtensions(
      permissions,
      { campaignType: 'Search', campaignObjective: 'WebsiteConversions' }
    ), [adExtensionsAssociationsFactory, permissions]);

  const suggestedAdExtensionsSections = useMemo(() => getAdExtensionsSections(suggestedObjectiveExtensions), [getAdExtensionsSections, suggestedObjectiveExtensions]);
  const otherAdExtensionsSections = useMemo(() => getAdExtensionsSections(otherObjectiveExtensions), [getAdExtensionsSections, otherObjectiveExtensions]);

  const [
    allEnhanceYourAdsSections,
    enhanceYourAdsExpandedSections,
    setEnhanceYourAdsExpandedSections,
  ] = useCollapseState(
    suggestedAdExtensionsSections.sectionsInfo.allSections /* all sections */,
    [] /* initially expanded sections */
  );

  const suggestedExtensionsSection = useMemo(() => (
    <>
      {suggestedAdExtensionsSections.panels.length > 0 &&
        <Collapse
          className="default-assets-section"
          allPanelItems={allEnhanceYourAdsSections}
          activeKeys={enhanceYourAdsExpandedSections}
          setActiveKeys={setEnhanceYourAdsExpandedSections}
          checkItems
          skipItemsTypeCheck
        >
          {suggestedAdExtensionsSections.panels}
        </Collapse>
      }
    </>
  ), [allEnhanceYourAdsSections, enhanceYourAdsExpandedSections, setEnhanceYourAdsExpandedSections, suggestedAdExtensionsSections]);

  const [
    allMoreAssetsSections,
    moreAssetsExpandedSections,
    setMoreAssetsExpandedSections,
  ] = useCollapseState(
    otherAdExtensionsSections.sectionsInfo.allSections /* all sections */,
    [] /* initially expanded sections */
  );

  const moreExtensionsSection = useMemo(() => (
    <>
      {otherAdExtensionsSections.panels.length > 0 &&
        <ValidatedBootstrapPanel
          defaultExpanded={false}
          className={classNames(getStyles('collapsiblePanel'), 'more-assets-panel')}
          render={() => (
            <>
              <BootstrapPanel.Heading>
                <BootstrapPanel.Title toggle>
                  {i18n.getString(_TL_('More assets'))}
                </BootstrapPanel.Title>
              </BootstrapPanel.Heading>
              <BootstrapPanel.Collapse>
                <BootstrapPanel.Body>
                  <div className={classNames('more-assets-section')}>
                    <Collapse allPanelItems={allMoreAssetsSections} checkItems skipItemsTypeCheck activeKeys={moreAssetsExpandedSections} setActiveKeys={setMoreAssetsExpandedSections}>
                      {otherAdExtensionsSections.panels}
                    </Collapse>
                  </div>
                </BootstrapPanel.Body>
              </BootstrapPanel.Collapse>
            </>
          )}
        />
      }
    </>
  ), [allMoreAssetsSections, getStyles, i18n, moreAssetsExpandedSections, otherAdExtensionsSections.panels, setMoreAssetsExpandedSections]);

  return loaded && (
    <div className={classNames(getStyles('stepContainer'), 'add-more-assets-step')}>
      <ActuationGroup actuationProps={{ isActive: false }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors: true }}>
            <>
              <StepHeader title={_TL_('Add more assets')} />
              <div className={classNames(getStyles('stepDescription'), 'step-description')}>
                <LearnMoreLink
                  topic="app54489"
                  text={i18n.getString(_TL_('Enhance your ads by including additional assets and information.'))}
                  linkText={i18n.getString(_TL_('Learn more'))}
                />
              </div>
            </>
            <div className="server-errors">
              <UnmappedFormErrors />
            </div>
            <div className="system-errors">
              <ErrorMessages messages={systemErrors} />
            </div>
            <div className="extensions">
              {suggestedExtensionsSection}
              {moreExtensionsSection}
            </div>
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

ExtensionsStepInternal.propTypes = {
  data: PropTypes.shape({
    campaignId: PropTypes.number,
    campaignName: PropTypes.string,
    adExtensionsAssociations: PropTypes.shape({}),
    loaded: PropTypes.bool,
  }),
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    serverErrors: PropTypes.arrayOf(PropTypes.shape({})),
    systemErrors: PropTypes.arrayOf(PropTypes.string),
  }),
  permissions: PropTypes.shape({
    IsImpressionTrackingUrlsEnabled: PropTypes.bool,
  }).isRequired,
  serverContext: PropTypes.shape({}).isRequired,
  wizardPerfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
};

ExtensionsStepInternal.defaultProps = {
  data: {
    adExtensionsAssociations: {},
    loaded: false,
  },
  error: null,
};

export const ExtensionsStep = withPermissions(withServerContext(ExtensionsStepInternal));
